import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Avatar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Edit, Delete, Add } from "@mui/icons-material";
import { get, post, put, del, postFiles } from "../../config/axios";
import toast from "react-hot-toast";

const UserTestimonial = () => {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const columns = [
    { field: "id", headerName: "S. No", width: 80 },
    {
      field: "image",
      headerName: "Image",
      width: 100,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          alt={params.row.name}
          sx={{ width: 40, height: 40 }}
        />
      )
    },
    { field: "name", headerName: "User Name", width: 150 },
    { field: "age", headerName: "Age", width: 150 },
    {
      field: "message",
      headerName: "Message",
      width: 550,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </div>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleOpenEditModal(params.row)}
          >
            <Edit />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDelete(params.row._id)}
          >
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  // Fetch testimonials
  const fetchTestimonials = async () => {
    try {
      const res = await get("/api/dashboard/website/testimonials");

      console.log("🔍 [UserTestimonial] Raw testimonials response:", res);

      // Handle error object from axios wrapper
      if (res?.response === "Failure") {
        console.error("❌ [UserTestimonial] API error:", res);
        toast.error(res?.message || res?.error || "Failed to fetch testimonials");
        setRows([]);
        return;
      }

      // Determine the actual data array:
      // - Some APIs may return an array directly
      // - Others may return { success, data: [...] }
      let data = Array.isArray(res) ? res : res?.data || res;

      if (!Array.isArray(data)) {
        console.error("❌ [UserTestimonial] Unexpected testimonials format:", data);
        toast.error("Unexpected response format while fetching testimonials");
        setRows([]);
        return;
      }

      const userTestimonials = data.filter((item) => item.type === "user");
      setRows(
        userTestimonials.map((item, index) => ({
          ...item,
          id: index + 1,
        }))
      );
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toast.error("Failed to fetch testimonials");
      setRows([]);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Reset form fields
  const resetForm = () => {
    setName("");
    setAge("");
    setMessage("");
    setImage(null);
    setImagePreview("");
    setEditingId(null);
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await postFiles("/api/app/user/uploadImage", formData);
      return response.data.url;
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      return null;
    }
  };

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Open modal for adding new testimonial
  const handleOpenAddModal = () => {
    resetForm();
    setOpenModal(true);
  };

  // Open modal for editing testimonial
  const handleOpenEditModal = (testimonial) => {
    setName(testimonial.name);
    setAge(testimonial.age || "");
    setMessage(testimonial.message);
    setImagePreview(testimonial.image || "");
    setEditingId(testimonial._id);
    setOpenModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setOpenModal(false);
    resetForm();
  };

  // Add or update testimonial
  const handleAddOrUpdate = async () => {
    if (!name || !message) {
      toast.error("Name and message are required!");
      return;
    }

    if (!editingId && !image) {
      toast.error("Image is required for new testimonials!");
      return;
    }

    try {
      let imageUrl = imagePreview;

      // Only upload if it's a new image (for new or existing testimonials)
      if (image) {
        imageUrl = await handleImageUpload(image);
        if (!imageUrl) return;
      }

      if (editingId) {
        await put(`/api/dashboard/website/testimonials/${editingId}`, {
          name,
          age,
          message,
          image: imageUrl
        });
        toast.success("Testimonial updated successfully.");
      } else {
        await post("/api/dashboard/website/testimonials", {
          name,
          age,
          message,
          image: imageUrl,
          type: "user"
        });
        toast.success("Testimonial added successfully.");
      }
      fetchTestimonials();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving testimonial:", error);
      toast.error("Error saving testimonial.");
    }
  };

  // Delete testimonial
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimonial?")) {
      try {
        await del(`/api/dashboard/website/testimonials/${id}`);
        fetchTestimonials();
        toast.success("Testimonial deleted successfully.");
      } catch (error) {
        console.error("Error deleting testimonial:", error);
        toast.error("Error deleting testimonial.");
      }
    }
  };

  return (
    <Layout>
      <Box sx={{ padding: "2rem" }}>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3
        }}>
          <Typography variant="h5">Manage User Testimonials</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAddModal}
          >
            Add Testimonial
          </Button>
        </Box>

        {/* Testimonial DataGrid */}
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10, 25]}
            getRowId={(row) => row._id}
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
            }}
          />
        </Paper>

        {/* Add/Edit Testimonial Modal */}
        <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogTitle>
            {editingId ? "Edit Testimonial" : "Add New Testimonial"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={1} sx={{ mt: 0 }}>
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
                <Avatar
                  src={imagePreview}
                  alt="Preview"
                  sx={{ width: 80, height: 80, mb: 2 }}
                />
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="testimonial-image-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="testimonial-image-upload">
                  <Button variant="outlined" component="span">
                    {imagePreview ? "Change Image" : "Upload Image"}
                  </Button>
                </label>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="User Name"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="User Age"
                  variant="outlined"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  margin="normal"
                  type="number"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Testimonial Message"
                  variant="outlined"
                  value={message}
                  onChange={(e) => {
                    if (e.target.value.length <= 150) {
                      setMessage(e.target.value);
                    }
                  }}
                  required
                  margin="normal"
                  inputProps={{
                    maxLength: 150
                  }}
                  helperText={`${message.length || 0}/150 characters`}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: 'right',
                      marginRight: 0,
                      marginTop: 0.5
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddOrUpdate}
              disabled={!name || !message || (!editingId && !image)}
            >
              {editingId ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default UserTestimonial;